import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useModalRegistration } from '@/hooks/useModalRegistration';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { validateUserForm, hasAnyError } from './userFormValidators';
import { User, Role } from '@/types/user';
import {
  useUpdateUser,
  useAssignUserRoles,
} from '@/hooks/react-query/useUsers';

interface UserEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  roles: Role[];
  onUserEdited: () => void;
}

function UserEditDialogForm({
  user,
  roles,
  onOpenChange,
  onUserEdited,
}: {
  user: User;
  roles: Role[];
  onOpenChange: (open: boolean) => void;
  onUserEdited: () => void;
}) {
  const initialFormData = useMemo(
    () => ({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      title: user.title || '',
      role_ids: user.roles ? user.roles.map((role) => role.id) : [],
    }),
    [user.first_name, user.last_name, user.email, user.title, user.roles]
  );

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateUserMutation = useUpdateUser();
  const assignUserRolesMutation = useAssignUserRoles();

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();

    setErrors({});

    const validationErrors = validateUserForm({
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      role_ids: formData.role_ids,
    });
    if (hasAnyError(validationErrors)) {
      setErrors(validationErrors as Record<string, string>);
      return;
    }

    try {
      // Update user details
      await updateUserMutation.mutateAsync({
        userId: user.id,
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          title: formData.title || undefined,
        },
      });

      // Update user roles
      await assignUserRolesMutation.mutateAsync({
        userId: user.id,
        roleIds: formData.role_ids,
      });

      onUserEdited();
    } catch {
      // Error is handled by the mutation hooks
      setErrors({ general: 'Failed to update user. Please try again.' });
    }
  };

  const toggleRole = (roleId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      role_ids: checked
        ? [...prev.role_ids, roleId]
        : prev.role_ids.filter((id) => id !== roleId),
    }));
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit User</DialogTitle>
        <DialogDescription>
          Update user information and role assignments.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-4 py-4">
          {errors.general && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.general}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    first_name: e.target.value,
                  }))
                }
                className={errors.first_name ? 'border-red-500' : ''}
              />
              {errors.first_name && (
                <p className="text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    last_name: e.target.value,
                  }))
                }
                className={errors.last_name ? 'border-red-500' : ''}
              />
              {errors.last_name && (
                <p className="text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title (Optional)</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="e.g., Fund Accountant"
            />
          </div>

          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="space-y-2 rounded border p-3">
              {roles.map((role) => (
                <label
                  key={role.id}
                  htmlFor={`role-${role.id}`}
                  className="flex items-center space-x-2 cursor-pointer"
                >
                  <Checkbox
                    id={`role-${role.id}`}
                    checked={formData.role_ids.includes(role.id)}
                    onCheckedChange={(checked) =>
                      toggleRole(role.id, checked === true)
                    }
                  />
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: role.color }}
                  />
                  <span>{role.display_name}</span>
                </label>
              ))}
            </div>
            {errors.role_ids && (
              <p className="text-sm text-red-600">{errors.role_ids}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={
              updateUserMutation.isPending || assignUserRolesMutation.isPending
            }
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              updateUserMutation.isPending || assignUserRolesMutation.isPending
            }
          >
            {updateUserMutation.isPending || assignUserRolesMutation.isPending
              ? 'Updating...'
              : 'Update User'}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

export function UserEditDialog({
  open,
  onOpenChange,
  user,
  roles,
  onUserEdited,
}: UserEditDialogProps) {
  // Modal registration for the edit dialog
  const { modalProps } = useModalRegistration(
    `user-edit-dialog-${user?.id || 'none'}`,
    open,
    () => onOpenChange(false)
  );

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" {...modalProps}>
        <UserEditDialogForm
          key={user.id}
          user={user}
          roles={roles}
          onOpenChange={onOpenChange}
          onUserEdited={onUserEdited}
        />
      </DialogContent>
    </Dialog>
  );
}
